<template>
  <div>
    <Button
      label="Create Contact"
      fluid
      @click="toggleOpen"
    >
      <template #icon>
        <Icon name="mdi-plus" />
      </template>
    </Button>

    <Dialog
      v-model:visible="visible"
      header="Create Contact"
      :style="{ width: '30rem' }"
      :modal="true"
    >
      <div
        v-if="isLoadingContactGroups"
        class="flex justify-center items-center min-h-[200px]"
      >
        <ProgressSpinner />
      </div>
      <div
        v-else
        class="flex flex-col gap-4"
      >
        <div
          v-if="similarContacts.length"
          class="space-y-2"
        >
          <p class="text-sm">
            Would you like to use one of these similar contacts?
          </p>
          <div
            v-for="contact in similarContacts"
            :key="contact.id"
          >
            <Message
              severity="secondary"
              :pt="{
                text: {
                  class: 'w-full',
                },
              }"
            >
              <div
                class="flex items-center justify-between"
              >
                <div class="text-sm space-y-1">
                  <p>{{ contact.firstName }} {{ contact.lastName }}</p>
                  <p class="text-xs">
                    {{ contact.email }}
                  </p>
                  <p class="text-xs">
                    {{ contact.phone }}
                  </p>
                </div>

                <Button
                  label="Use this contact"
                  size="small"
                  outlined
                  @click="useSimilarContact(contact)"
                />
              </div>
            </Message>
          </div>
        </div>

        <BaseInputLabel
          label="First Name"
          :isRequired="true"
          :isErrored="errors.firstName"
          errorMessage="First name is required"
        >
          <InputText
            v-model="contactData.firstName"
            fluid
            :invalid="errors.firstName"
          />
        </BaseInputLabel>

        <BaseInputLabel
          label="Last Name"
          :isRequired="true"
          :isErrored="errors.lastName"
          errorMessage="Last name is required"
        >
          <InputText
            v-model="contactData.lastName"
            fluid
            :invalid="errors.lastName"
          />
        </BaseInputLabel>

        <BaseInputLabel
          label="Email"
          :isRequired="false"
          :isErrored="errors.email"
          errorMessage="Valid email or phone is required"
        >
          <BaseEmailInputWithValidation
            ref="emailInputRef"
            v-model:inputValue="contactData.email"
            v-model:isErrored="errors.email"
          />
        </BaseInputLabel>

        <BaseInputLabel
          label="Phone"
          :isRequired="false"
          :isErrored="errors.phone"
          errorMessage="Valid email or phone is required"
        >
          <BasePhoneInputWithValidation
            ref="phoneInputRef"
            v-model:inputValue="contactData.phone"
            v-model:isErrored="errors.phone"
          />
        </BaseInputLabel>

        <BaseInputLabel
          label="Address"
          :isRequired="false"
        >
          <BaseGoogleAddress
            v-model:address="contactData.address"
            :disabled-input-field="false"
          />
        </BaseInputLabel>

        <BaseInputLabel
          label="Groups"
          :isRequired="false"
        >
          <MultiSelect
            id="groups"
            v-model="contactData.contactGroupIds"
            :options="contactGroups"
            optionLabel="name"
            optionValue="id"
            placeholder="Select Groups"
            class="w-full"
          />
        </BaseInputLabel>
      </div>

      <div
        v-if="!isLoadingContactGroups"
        class="flex justify-end gap-2 mt-4"
      >
        <Button
          outlined
          label="Cancel"
          icon="pi pi-times"
          :disabled="loading"
          @click="hideDialog"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          :loading="loading"
          @click="saveContact"
        />
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { Contact } from '@prisma/client'
import { useContacts } from '~/composables/useContacts'

const emit = defineEmits(['fetchContacts'])

const { createContact, fetchContactGroups } = useContacts()
const toast = useToast()

// Contacts state
const similarContacts = ref<Contact[]>([])
const { fetchSimilarContacts } = useContacts()

const useSimilarContact = (contact: Contact) => {
  emit('fetchContacts', contact)
  hideDialog()
}

const visible = ref(false)
const loading = ref(false)
const submitted = ref(false)
const contactGroups = ref<any[]>([])

const contactData = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  contactGroupIds: [],
  address: '',
})

const errors = ref({
  firstName: false,
  lastName: false,
  email: false,
  phone: false,
  address: false,
})

const hideDialog = () => {
  visible.value = false
  submitted.value = false
  similarContacts.value = []
  resetForm()
}

const resetForm = () => {
  contactData.value = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    contactGroupIds: [],
    address: '',
  }
  errors.value = {
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    address: false,
  }
}

// Email Input  Component
const emailInputRef = ref()

// Phone Input Component
const phoneInputRef = ref()

// Validate Email
const validateEmail = async (email: string) => {
  await emailInputRef.value.validate(email)
  return emailInputRef.value.isValid
}

// Validate Phone
const formattedContactPhone = ref<string>()

const validatePhone = (phone: string) => {
  phoneInputRef.value.formatAndValidate(phone.toString())

  if (phoneInputRef.value.isValid) {
    formattedContactPhone.value = phoneInputRef.value.formattedInputNumber.number
  }

  return phoneInputRef.value.isValid
}

const validateForm = async (): Promise<boolean> => {
  errors.value = {
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    address: false,
  }

  if (contactData.value.email) {
    errors.value.email
      = !await validateEmail(contactData.value.email)
  }
  errors.value.firstName = !contactData.value.firstName.trim()
  errors.value.lastName = !contactData.value.lastName.trim()
  if (contactData.value.phone) {
    errors.value.phone = !validatePhone(contactData.value.phone)
  }

  return !Object.values(errors.value).some(Boolean)
}

// Debounce timer - When user types in the filter input, this will help to limit the number of API calls
// Check handleFilter function
let searchTimeout: NodeJS.Timeout | null = null

// Handle search with debounce
const handleSearch = () => {
  // Clear previous timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  // Debounce search for 500ms
  searchTimeout = setTimeout(async () => {
    similarContacts.value = []

    const res = await fetchSimilarContacts(
      contactData.value.firstName,
      contactData.value.lastName,
      contactData.value.email,
      contactData.value.phone,
    )

    similarContacts.value = res.data || []
  }, 500)
}

watch(contactData, (newValue) => {
  if (
    (newValue.firstName.trim() && newValue.lastName.trim())
    || newValue.email.trim()
    || newValue.phone.trim()
  ) {
    handleSearch()
  }
  else {
    similarContacts.value = []
  }
}, { deep: true })

const saveContact = async () => {
  submitted.value = true
  loading.value = true

  try {
    const isValid = await validateForm()

    if (!isValid) {
      loading.value = false
      throw Error('Failed to create contact - Invalid form data')
    }

    if (!contactData.value.email && !contactData.value.phone) {
      loading.value = false
      errors.value.email = true
      errors.value.phone = true
      throw Error('Valid email or phone is required')
    }

    const { data, error } = await createContact({
      firstName: contactData.value.firstName,
      lastName: contactData.value.lastName,
      email: contactData.value.email || `cimpro.app+` + `${Date.now()}` + `@gmail.com`,
      phone: formattedContactPhone.value,
      contactGroupIds: contactData.value.contactGroupIds,
      address: contactData.value.address,
    })

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create contact',
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Contact created successfully',
      life: 3000,
    })

    hideDialog()
    emit('fetchContacts', data)
  }
  catch (error) {
    console.error('Error creating contact:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.message || 'Failed to create contact',
      life: 3000,
    })
  }
  finally {
    loading.value = false
  }
}
const isLoadingContactGroups = ref(false)

const loadContactGroups = async () => {
  isLoadingContactGroups.value = true
  try {
    const { data, error } = await fetchContactGroups()

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch contact groups',
        life: 3000,
      })
      return
    }

    contactGroups.value = data || []
  }
  catch (error) {
    console.error('Error fetching contact groups:', error)
  }
  finally {
    isLoadingContactGroups.value = false
  }
}

const toggleOpen = async () => {
  visible.value = true
  await loadContactGroups()
}

defineExpose({ toggleOpen })
</script>
